import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { formatCurrency } from '../utils/formatters';
import { SplitType } from '../components/expense/ParticipantList';
import { ExpenseType } from '../components/expense/ExpenseTypeSelect';
import { ParticipantWithAmount } from './mutations/useAddExpense';
import { currencies, CurrencyCode } from '../constants/currencies';

// KST 등 로컬 타임존 기준으로 HTML date input 형식(YYYY-MM-DD)을 안전하게 만든다.
// NOTE: Date#toISOString()는 UTC 기준이라 자정 직후 날짜가 하루 전으로 보일 수 있음.
const toLocalDateInputValue = (value: Date | string = new Date()) => {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

interface ExpenseFormData {
  title: string;
  amount: number;
  paidBy: string;
  participants: Set<string>;
  formattedAmount: string;
  receiverId?: string;
  spentAt: string;
  currency: CurrencyCode;
  exchangeRate: number;
}

interface UseExpenseFormProps {
  members: any[] | undefined;
  currentUser: any;
  defaultCurrency: CurrencyCode;
  currencyMap: Map<CurrencyCode, number>;
  lastCurrency?: CurrencyCode;
}

interface ExpenseEditData {
  title: string;
  amount: number;
  paidBy: string;
  currency: CurrencyCode;
  exchangeRate: number;
  spentAt: string;
  participants: ParticipantWithAmount[];
  isTransfer: boolean;
  splitType?: SplitType;
}

export const useExpenseForm = ({ members, currentUser, defaultCurrency, currencyMap, lastCurrency }: UseExpenseFormProps) => {
  // 지출 유형 상태
  const [expenseType, setExpenseType] = useState<ExpenseType>(
    '지출기록'
  );
  // 금액 분배 관련 상태
  const [splitType, setSplitType] = useState<SplitType>('equally');
  const [multipliers, setMultipliers] = useState<Record<string, number>>({});
  const [amountForDisplay, setAmountForDisplay] = useState<string>('');
  const [manualAmounts, setManualAmounts] = useState<Record<string, number>>({});
  const [formattedManualAmounts, setFormattedManualAmounts] = useState<Record<string, string>>({});
  const [editInitialCurrency, setEditInitialCurrency] = useState<CurrencyCode>(defaultCurrency);
  const [exchangeRateInput, setExchangeRateInput] = useState<string>('');
  // 수정 모드 여부를 추적하는 상태 추가
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [tempTitle, setTempTitle] = useState<string>('');

  const dontChangeExchangeRateAtInitialRender = useRef(true)
  
  // 폼 설정
  const { 
    register, 
    handleSubmit, 
    control,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm<ExpenseFormData>({
    defaultValues: {
      title: '',
      amount: undefined,
      currency: lastCurrency || defaultCurrency,
      paidBy: members?.find(m => m.user_id === currentUser?.id)?.id || '',
      participants: new Set<string>(),
      receiverId: '',
      spentAt: toLocalDateInputValue(),
      exchangeRate: 1
    }
  });

  const title = watch('title');
  const amount = watch('amount') || 0;
  const participants = watch('participants');
  const paidBy = watch('paidBy');
  const spentAt = watch('spentAt');
  const currency = watch('currency');
  const exchangeRate = watch('exchangeRate');

  // 날짜 변경 처리
  const handleSpentAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('spentAt', e.target.value);
  };

  useEffect(() => {
    // 수정 모드에서는 기본 통화로 재설정하지 않음
    if (isEditMode) return;
    
    setValue('currency', lastCurrency || defaultCurrency);
  }, [defaultCurrency, isEditMode, setValue, lastCurrency]);

  useEffect(()=>{
    // 수정 모드에서는 통화 변경 시 금액을 초기화하지 않음
    if (isEditMode) {
      onInputBlur()
      return;
    }
    
    setAmountForDisplay('')
    setValue('amount', 0)
  }, [currency, isEditMode])

  useEffect(() => {
    let newExchangeRate = 1;
    
    if (isEditMode && currency === editInitialCurrency && dontChangeExchangeRateAtInitialRender.current) {
      dontChangeExchangeRateAtInitialRender.current = false
      return
    }
    if (currency !== defaultCurrency) {

      // 두 통화 사이의 환율 계산
      const currencyRate = currencyMap.get(currency) || 1;
      const defaultCurrencyRate = currencyMap.get(defaultCurrency) || 1;
      
      // currency 1단위당 defaultCurrency 단위 계산
      newExchangeRate = currencyRate / defaultCurrencyRate;
      newExchangeRate = Number(parseFloat(newExchangeRate.toPrecision(5)));
    }
    
    // 현재 값과 다를 때만 업데이트
    if (exchangeRate !== newExchangeRate) {
      setValue('exchangeRate', newExchangeRate);
      setExchangeRateInput(newExchangeRate.toString());
    }
  }, [currency, defaultCurrency, isEditMode]);

  // 그룹 로딩시 모든 멤버를 참가자로 기본 설정
  useEffect(() => {
    if (members?.length) {
      // 처음 로드될 때만 실행되도록 참조 체크 추가
      const participantIds = new Set(members.map(member => member.id));
      
      // 현재 상태의 participants 집합 크기와 비교
      const currentParticipants = watch('participants');
      if (!currentParticipants || currentParticipants.size === 0) {
        setValue('participants', participantIds, { shouldDirty: false });
        
        // 결제자를 현재 사용자로 기본 설정
        const currentMember = members.find(m => m.user_id === currentUser?.id);
        if (currentMember && !watch('paidBy')) {
          setValue('paidBy', currentMember.id, { shouldDirty: false });
        }

        // 배수 입력 초기화
        const initialMultipliers: Record<string, number> = {};
        members.forEach(member => {
          initialMultipliers[member.id] = 1;
        });
        setMultipliers(initialMultipliers);

        // 수동 입력 초기화
        const initialManualAmounts: Record<string, number> = {};
        members.forEach(member => {
          initialManualAmounts[member.id] = 0;
        });
        setManualAmounts(initialManualAmounts);
      }
    }
  }, [members, setValue, currentUser, watch]);

  // 지출 유형 변경 시 폼 재설정
  useEffect(() => {
    if (expenseType === '송금기록') {
      setTempTitle(title || '')
      setValue('title', '개인송금');
    } else {
      setValue('title', tempTitle);
    }
  }, [expenseType, setValue]);

  // 분배 방식 변경 시 데이터 초기화
  useEffect(() => {
    // 수정 모드일 때는 초기화 로직을 실행하지 않음
    if (isEditMode) {
      return
    }

    if (members?.length) {
      // 모든 멤버를 참가자로 설정
      const participantIds = new Set(members.map(member => member.id));
      setValue('participants', participantIds, { shouldDirty: false });

      if (splitType === 'parts') {
        // 배수 입력 초기화
        const initialMultipliers: Record<string, number> = {};
        members.forEach(member => {
          initialMultipliers[member.id] = 1;
        });
        setMultipliers(initialMultipliers);
      } else if (splitType === 'amounts') {
        // 수동 입력 초기화 - 0으로 초기화
        const newManualAmounts: Record<string, number> = {};
        const newFormattedAmounts: Record<string, string> = {};
        
        members.forEach(member => {
          newManualAmounts[member.id] = 0;
          newFormattedAmounts[member.id] = formatCurrency(0, currency);
        });
        
        setManualAmounts(newManualAmounts);
        setFormattedManualAmounts(newFormattedAmounts);
      }
    }
  }, [splitType, members, setValue, amount, currency, isEditMode]);


  function onExchangeRateChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    setExchangeRateInput(value);
  }

  function onExchangeRateBlur() {
    const precisionValue = Number(parseFloat(exchangeRateInput).toPrecision(5));
    if (isNaN(precisionValue)) {
      setValue('exchangeRate', currencyMap.get(currency) || 1);
      setExchangeRateInput(currencyMap.get(currency)?.toString() || '1');
    } else {
      setValue('exchangeRate', precisionValue);
      setExchangeRateInput(precisionValue.toString());
    }
  }

  // 수동 입력 핸들러
  const handleManualAmountInputChange = (memberId: string, value: string) => {
    // 현재 입력 중인 값을 포맷된 상태로 저장
    const newFormattedAmounts = { ...formattedManualAmounts };
    newFormattedAmounts[memberId] = value;
    setFormattedManualAmounts(newFormattedAmounts);
    
    // 입력값 처리
    const formattedValue = handleCurrencyInput(value, currency);
    newFormattedAmounts[memberId] = formattedValue;
    setFormattedManualAmounts(newFormattedAmounts);
    
    // 숫자값 계산
    const numericValue = parseFloat(formattedValue.replace(/[^\d.-]/g, ''));
    const newAmount = isNaN(numericValue) ? 0 : numericValue;
    
    const newManualAmounts = { ...manualAmounts };
    newManualAmounts[memberId] = newAmount;
    setManualAmounts(newManualAmounts);
    
    // 0원이면 참가자에서 제외
    const newParticipants = new Set(participants);
    if (newAmount === 0) {
      newParticipants.delete(memberId);
    } else if (!newParticipants.has(memberId)) {
      newParticipants.add(memberId);
    }
    
    setValue('participants', newParticipants);
  };
  
  // 포커스를 잃었을 때 포맷팅 적용
  const handleManualAmountBlur = (memberId: string) => {
    const myamount = manualAmounts[memberId] || 0;
    const formatted = formatCurrencyForDisplay(myamount, currency).toString();
    
    const newFormattedAmounts = { ...formattedManualAmounts };
    newFormattedAmounts[memberId] = formatted;

    // 0 인 값이 하나 남았을때
    const zeroAmounts = Object.keys(manualAmounts).filter(id => manualAmounts[id] === 0);
    if (zeroAmounts.length === 1) {
      const onlyMemberId = zeroAmounts[0];
      const otherSum = Object.keys(manualAmounts)
        .filter(id => id !== onlyMemberId)
        .reduce((sum, id) => sum + (manualAmounts[id] || 0), 0);

      const remaining = amount - otherSum;
      if (remaining > 0) {
        const newManualAmounts = {...manualAmounts}
        newManualAmounts[onlyMemberId] = remaining
        setManualAmounts(newManualAmounts)
        newFormattedAmounts[onlyMemberId] = formatCurrencyForDisplay(remaining, currency).toString();
      }
    }

    setFormattedManualAmounts(newFormattedAmounts);
  };

  // 체크박스 토글 처리
  const toggleParticipant = (memberId: string) => {
    const newParticipants = new Set(participants);
    
    if (newParticipants.has(memberId)) {
      // 참가자 제외
      newParticipants.delete(memberId);
      
      // 배수입력 모드일 경우 배수도 0으로 설정
      if (splitType === 'parts') {
        const newMultipliers = { ...multipliers };
        newMultipliers[memberId] = 0;
        setMultipliers(newMultipliers);
      }
    } else {
      // 참가자 추가
      newParticipants.add(memberId);
      
      // 배수입력 모드일 경우 배수를 1로 초기화
      if (splitType === 'parts') {
        const newMultipliers = { ...multipliers };
        newMultipliers[memberId] = 1;
        setMultipliers(newMultipliers);
      }
    }
    
    setValue('participants', newParticipants);
  };

  // 배수 증가/감소 처리
  const adjustMultiplier = (memberId: string, change: number) => {
    const newMultipliers = { ...multipliers };
    const currentValue = newMultipliers[memberId] || 0;
    
    // 새 배수값 계산
    let newValue = currentValue + change;
    
    // 배수값이 0 이하가 될 경우
    if (newValue <= 0) {
      newValue = 0;
      // 참가자에서 제외
      const newParticipants = new Set(participants);
      newParticipants.delete(memberId);
      setValue('participants', newParticipants);
    } else {
      // 배수값이 양수인데 참가자에 없는 경우 참가자에 추가
      if (!participants.has(memberId)) {
        const newParticipants = new Set(participants);
        newParticipants.add(memberId);
        setValue('participants', newParticipants);
      }
    }
    
    // 배수값 업데이트
    newMultipliers[memberId] = newValue;
    setMultipliers(newMultipliers);
  };

  // 각 참가자의 분담금액 계산
  const calculateShare = (memberId: string) => {
    if (!participants.has(memberId) || amount === 0) return 0;
    
    const decimalPlaces = currencies[currency].maximumFractionDigits;
    const factor = Math.pow(10, decimalPlaces);
    
    if (splitType === 'equally') {
      return Math.round((amount / participants.size) * factor) / factor;
    } else if (splitType === 'parts') {
      // 전체 배수 합계 계산
      let totalMultiplier = 0;
      participants.forEach((pid: string) => {
        totalMultiplier += multipliers[pid] || 1;
      });
      
      if (totalMultiplier === 0) return 0;
      return Math.round((amount * (multipliers[memberId] || 1) / totalMultiplier) * factor) / factor;
    } else if (splitType === 'amounts') {
      // 수동입력
      return manualAmounts[memberId] || 0;
    }
  };

  function handleCurrencyInput(inputValue: string, currencyCode: CurrencyCode) {
    // currencies 객체에서 통화 정보 가져오기
    const currency = currencies[currencyCode];
    if (!currency) return inputValue;
    
    // 입력값에서 숫자, 소수점, 마이너스 부호만 남김
    const cleanedInput = inputValue.replace(/[^\d.-]/g, '');

    // 입력 중인 특수 상태 확인
    const isTrailingDecimalPoint = cleanedInput.endsWith('.');
    
    // 빈 문자열이거나 특수 상태인 경우 그대로 반환
    if (!cleanedInput) {
      return '';
    }
    
    // 파싱 시도
    const numericValue = parseFloat(cleanedInput);

    // 유효하지 않은 숫자인 경우
    if (isNaN(numericValue)) {
      return '';
    }
    
    // 포맷팅을 위한 옵션
    const options = {
      style: 'decimal' as const, // 통화 기호 없이 숫자만 포맷
      minimumFractionDigits: 0,
      maximumFractionDigits: currency.maximumFractionDigits,
    };
    
    // 소수점으로 끝나는 경우 특별 처리
    if (isTrailingDecimalPoint && currency.maximumFractionDigits > 0) {
      const formatted = new Intl.NumberFormat('en-US', options).format(numericValue);
      return formatted + '.';
    }
    
    // 소수점이 있는 경우 소수 자릿수 유지
    const decimalParts = cleanedInput.split('.');
    if (decimalParts.length > 1) {
      const decimalDigits = decimalParts[1].length;
      options.minimumFractionDigits = Math.min(decimalDigits, currency.maximumFractionDigits);
    }
    
    // 숫자 포맷팅
    const formatted = new Intl.NumberFormat('en-US', options).format(numericValue);
    return formatted;
  }

  function formatCurrencyForDisplay(value: string | number, currencyCode: CurrencyCode) {
    const currency = currencies[currencyCode];
    if (!currency) return value;
    
    // 문자열을 숫자로 변환
    let numericValue;
    
    if (typeof value === 'string') {
      numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
    } else {
      numericValue = value;
    }
    
    if (isNaN(numericValue)) return '';
    
    // 최종 표시를 위한 통화 포맷
    return currency.symbol + new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: currency.minimumFractionDigits,
      maximumFractionDigits: currency.maximumFractionDigits
    }).format(numericValue);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const rawValue = e.target.value;
    // 포맷팅된 값을 입력 필드에 적용 (소수점 입력 중 상태 유지)
    const formattedForInput = handleCurrencyInput(rawValue, currency);
    
    // 필요한 경우 다른 요소에 표시용 포맷 적용
    const formattedForDisplay = formatCurrencyForDisplay(formattedForInput, currency);

    
    // 내부 계산용 숫자값 (필요한 경우)
    const numericValue = parseFloat(formattedForInput.replace(/[^\d.-]/g, ''));

    setValue('amount', numericValue);
    setAmountForDisplay(formattedForInput);

    return {
      formattedForInput,
      formattedForDisplay,
      numericValue: isNaN(numericValue) ? 0 : numericValue
    };
  }

  function onInputBlur() {
    const formatted = formatCurrencyForDisplay(amountForDisplay, currency)
    setAmountForDisplay(formatted.toString())
  }

  // 지출 유형 변경 처리
  const handleExpenseTypeChange = (type: ExpenseType) => {
    setExpenseType(type);
  };

  // 분배 방식 변경 처리
  const handleSplitTypeChange = (type: SplitType) => {
    setSplitType(type);
  };

  // 참가자 데이터를 API 요청 형식으로 변환
  const getParticipantsWithAmount = (): any[] => {
    const participantsWithAmount: any[] = [];
    
    participants.forEach((memberId: string) => {
      let participantAmount = 0;
      
      participantAmount = calculateShare(memberId)
      
      participantsWithAmount.push({
        id: memberId,
        amount: participantAmount,
      });
    });
    
    return participantsWithAmount;
  };

  // 수동 입력 검증
  const validateManualAmounts = (): { isValid: boolean; message: string } => {
    if (splitType !== 'amounts' || !amount || participants.size === 0) {
      return { isValid: true, message: '' };
    }
    
    let totalManualAmount = 0;
    participants.forEach((memberId: string) => {
      totalManualAmount += manualAmounts[memberId] || 0;
    });
    
    if (Math.abs(totalManualAmount - amount) > 0.01) {
      return { 
        isValid: false, 
        message: `입력하신 금액의 합계(${formatCurrency(totalManualAmount, currency)})가 총 금액(${formatCurrency(amount, currency)})과 일치하지 않습니다.`
      };
    }
    
    return { isValid: true, message: '' };
  };

  // 수정 모드 사용 시 기존 데이터 초기화
  const setInitialDataForEdit = (data: ExpenseEditData) => {
    // 수정 모드로 설정
    setIsEditMode(true);
    
    // 기본 폼 필드 설정
    setValue('title', data.title);
    setValue('amount', data.amount);
    setValue('paidBy', data.paidBy);
    setValue('currency', data.currency);
    setValue('exchangeRate', data.exchangeRate);
    setExchangeRateInput(data.exchangeRate.toString());
    setValue('spentAt', toLocalDateInputValue(data.spentAt));
    
    // 금액 표시 포맷 설정 (이 부분이 중요합니다)
    setAmountForDisplay(formatCurrencyForDisplay(data.amount, data.currency).toString());

    setEditInitialCurrency(data.currency);
    
    // 지출 유형 설정
    setExpenseType(data.isTransfer ? '송금기록' : '지출기록');
    
    // 송금기록인 경우 수신자 설정
    if (data.isTransfer && data.participants.length > 0) {
      setValue('receiverId', data.participants[0].id);
    }
    
    // 지출기록이고 splitType이 있는 경우 분배 방식 설정
    if (!data.isTransfer && data.splitType) {
      setSplitType(data.splitType);
    }
    
    // 참가자 목록 초기화
    const participantIds = new Set(data.participants.map(p => p.id));
    setValue('participants', participantIds);
    const memberIds = new Set(members.map(member => member.id));
    
    // splitType에 따른 초기화
    if (data.splitType === 'amounts' && !data.isTransfer) {
      // 수동 입력 모드 초기화
      const newManualAmounts: Record<string, number> = {};
      const newFormattedAmounts: Record<string, string> = {};
      
      data.participants.forEach(participant => {
        newManualAmounts[participant.id] = participant.amount;
        newFormattedAmounts[participant.id] = formatCurrency(participant.amount, data.currency);
      });

      memberIds.forEach(memberId => {
        if (!newManualAmounts[memberId]) {
          newManualAmounts[memberId] = 0;
          newFormattedAmounts[memberId] = formatCurrency(0, data.currency);
        }
      })
      
      setManualAmounts(newManualAmounts);
      setFormattedManualAmounts(newFormattedAmounts);
    } else if (data.splitType === 'parts' && !data.isTransfer) {
      // 배수 입력 모드 계산
      // 총 금액과 참가자별 금액을 기반으로 비율 계산
      const totalAmount = data.amount;
      if (totalAmount > 0 && data.participants.length > 0) {
        // 1. 각 참가자의 금액에 따른 비율 계산
        const ratios: Record<string, number> = {};
        let minRatio = Infinity;
        
        data.participants.forEach(participant => {
          const ratio = participant.amount / totalAmount;
          ratios[participant.id] = ratio;
          
          // 가장 작은 비율 찾기
          if (ratio < minRatio) {
            minRatio = ratio;
          }
        });
        
        // 2. 배수 계산 (가장 작은 비율을 가진 참가자가 1배수가 되도록)
        const newMultipliers: Record<string, number> = {};
        
        data.participants.forEach(participant => {
          // 가장 작은 비율 대비 상대적 배수 계산 후 정수로 올림
          const relativeMultiplier = ratios[participant.id] / minRatio;
          // 최소 1, 정수로 올림
          newMultipliers[participant.id] = Math.max(1, Math.round(relativeMultiplier));
        });

        memberIds.forEach(memberId => {
          if (!newMultipliers[memberId]) {
            newMultipliers[memberId] = 0
          }
        })
        
        setMultipliers(newMultipliers);
      }
    }
  };

  return {
    // 상태
    expenseType,
    splitType,
    multipliers,
    manualAmounts,
    formattedManualAmounts,
    amount,
    amountForDisplay,
    participants,
    paidBy,
    spentAt,
    errors,
    currency,
    exchangeRate,
    exchangeRateInput,
    isEditMode,
    
    // 폼 관련
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    
    // 함수
    toggleParticipant,
    adjustMultiplier,
    handleManualAmountInputChange,
    handleManualAmountBlur,
    calculateShare,
    onInputChange,
    handleExpenseTypeChange,
    handleSplitTypeChange,
    getParticipantsWithAmount,
    validateManualAmounts,
    handleSpentAtChange,
    onInputBlur,
    onExchangeRateChange,
    onExchangeRateBlur,
    setInitialDataForEdit
  };
}; 